import { TAG_TYPES } from '../../../../Shared/constants/commonConstants';
import { UserRoles } from '../../../../Shared/constants/userRoles';
import {
  ADVERTISER_NOT_FOUND_STRING,
  ADVERTISER_NOT_FOUND_TRANSLATION_KEY,
  FORM_FIELDS,
  RO_VALUE_MAX_LENGTH,
} from '../constants/FormFieldsConstants';
import { Advertiser, AEUser, AMUser, TypeOfPurchase } from '../interface/ApiInterfaces';
import {
  BookingFormData, BookingFormDataErrors, FormDataFields, FormHandlers
} from '../interface/BookingFormInterface';
import { getTranslatedGtmStatusOptions } from '../services/helpers';
import { getBrandConfirmationOptions, getCreativesOptions, getRoOptions } from './dropDownConfig';

export const createFormConfig = (
  formData: BookingFormData,
  handlers: FormHandlers,
  data: FormDataFields,
  fieldErrors: BookingFormDataErrors,
  isEditMode = false,
  t: (key: string) => string,
  userRole?: string
) => {
  const baseFields = [
    {
      id: 'ae-name-field',
      type: TAG_TYPES.DROPDOWN_TAG_TYPE,
      label: 'ae-name-label',
      value: formData.aeName,
      onChange: handlers.handleAeNameChange,
      placeholder: 'select-ae-user',
      disabled: isEditMode || userRole === UserRoles.ACCOUNT_EXECUTIVE, // Disable AE name in edit mode or if user is AE
      options: (data.aeUsers || []).map((user: AEUser) => ({
        id: `ae-name-${user.userId}`,
        value: user.userId,
        label: `${user.firstName} ${user.lastName} (${user.userId})`,
      })),
      error: fieldErrors.aeName,
    },
    {
      id: 'am-name-field',
      type: TAG_TYPES.DROPDOWN_TAG_TYPE,
      label: 'am-name-label',
      value: formData.amName,
      onChange: (value: string) => handlers.handleFieldChange(FORM_FIELDS.AM_NAME, value),
      placeholder: 'select-am-user',
      disabled: isEditMode || userRole === UserRoles.ACCOUNT_MANAGER, // Disable AM name in edit mode or if user is AM
      options: (data.amUsers || []).map((user: AMUser) => ({
        id: `am-name-${user.userId}`,
        value: user.userId,
        label: `${user.firstName} ${user.lastName} (${user.userId})`,
      })),
      error: fieldErrors.amName,
    },
    {
      id: 'category-field',
      type: TAG_TYPES.INPUT_TAG_TYPE,
      label: 'category-label',
      value: formData.category,
      placeholder: 'category-placeholder',
      disabled: true, // Always disabled as it's auto-populated
      error: fieldErrors.category,
    },
    {
      id: 'select-org-field',
      type: TAG_TYPES.INPUT_TAG_TYPE,
      label: 'select-org-label',
      value: formData.org,
      placeholder: 'select-rodeo-org',
      disabled: true, // Org is auto-populated from AE's orgName
      error: fieldErrors.org,
    },
    {
      id: 'rodeo-advertiser-field',
      type: TAG_TYPES.DROPDOWN_TAG_TYPE,
      label: 'rodeo-advertiser-name-label',
      value: formData.rodeoAdName,
      onChange: handlers.handleRodeoAdvertiserChange,
      placeholder: 'select-rodeo-advertiser-name',
      disabled: isEditMode ? false : !formData.category || !formData.org,
      options: (data.advertisersList || []).map(
        (advertiser: Advertiser, index: number) => ({
          id: `advertiser-${index}`,
          value: advertiser.advertiserName,
          label: advertiser.advertiserName,
        })
      ),
      persistentBottomOption: {
        id: 'advertiser-not-found',
        value: ADVERTISER_NOT_FOUND_STRING,
        label: t(ADVERTISER_NOT_FOUND_TRANSLATION_KEY),
      },
      error: fieldErrors.rodeoAdName,
    },
    {
      id: 'brand-field',
      type: TAG_TYPES.INPUT_TAG_TYPE,
      label: 'brand-label',
      value: formData.brand,
      onChange: (value: string) =>
        handlers.handleFieldChange(FORM_FIELDS.BRAND, value),
      placeholder: 'brand-placeholder',
      disabled: formData.rodeoAdName !== ADVERTISER_NOT_FOUND_STRING,
      error: fieldErrors.brand,
    },
    {
      id: 'purchase-type-field',
      type: TAG_TYPES.DROPDOWN_TAG_TYPE,
      label: 'type-of-purchase-label',
      value: formData.typeOfPurchase,
      onChange: (value: string) => handlers.handleFieldChange(FORM_FIELDS.TYPE_OF_PURCHASE, value),
      placeholder: 'select-type-of-purchase',
      disabled: isEditMode, // Disable type of purchase in edit mode
      options: (data.typeOfPurchaseList || []).map(
        (item: TypeOfPurchase, index: number) => ({
          id: `purchase-type-${index}`,
          value: item.purchaseType,
          label: item.purchaseType,
        })
      ),
      error: fieldErrors.typeOfPurchase,
    },
    {
      id: 'billing-value-field',
      type: TAG_TYPES.INPUT_TAG_TYPE,
      label: 'exact-billing-value-label',
      value: !formData.exactBillingValue ? '' : formData.exactBillingValue.toString(),
      onChange: (value: string) => {
        const cleanValue = value.replace(/\D/g, '');
        if (cleanValue.length <= RO_VALUE_MAX_LENGTH) {
          handlers.handleFieldChange(FORM_FIELDS.EXACT_BILLING_VALUE, cleanValue);
        }
      },
      placeholder: 'enter-billing-ro-value',
      maxLength: RO_VALUE_MAX_LENGTH,
      validation: (value: string) => /^\d*$/.test(value),
      error: fieldErrors.exactBillingValue,
    },
    {
      id: 'sf-opportunity-field',
      type: TAG_TYPES.INPUT_TAG_TYPE,
      label: 'sf-opportunity-id-label',
      value: formData.sfOpportunityId,
      onChange: (value: string) => {
        if (value.length <= 18) {
          handlers.handleFieldChange(FORM_FIELDS.SF_OPPORTUNITY_ID, value);
        }
      },
      placeholder: 'enter-sf-opportunity-id',
      maxLength: 18,
      validation: (value: string) => value.length <= 18,
      error: fieldErrors.sfOpportunityId,
    },
    {
      id: 'ro-in-field',
      type: TAG_TYPES.DROPDOWN_TAG_TYPE,
      label: 'ro-in-label',
      value: formData.ro,
      onChange: (value: string) => handlers.handleFieldChange(FORM_FIELDS.RO, value),
      placeholder: 'select-ro-in',
      optional: true,
      options: getRoOptions(t),
      error: fieldErrors.ro,
    },
    {
      id: 'brand-confirmation-field',
      type: TAG_TYPES.DROPDOWN_TAG_TYPE,
      label: 'brand-confirmation-label',
      value: formData.brandConfirmation,
      onChange: (value: string) => handlers.handleFieldChange(FORM_FIELDS.BRAND_CONFIRMATION, value),
      placeholder: 'select-brand-confirmation',
      optional: true,
      options: getBrandConfirmationOptions(t),
      error: fieldErrors.brandConfirmation,
    },
    {
      id: 'comments-by-poc-field',
      type: TAG_TYPES.INPUT_TAG_TYPE,
      label: 'comments-by-poc-label',
      value: formData.commentsByPoc,
      onChange: (value: string) => {
        if (value.length <= 250)
          handlers.handleFieldChange(FORM_FIELDS.COMMENTS_BY_POC, value);
      },
      placeholder: 'enter-comments-by-poc',
      optional: true,
      maxLength: 250,
      validation: (value: string) => value.length <= 250,
      disabled: !(
        userRole === UserRoles.SUPER_ADMIN || userRole === UserRoles.ADMIN
      ),
      error: fieldErrors.commentsByPoc,
    },
    {
      id: 'select-creatives',
      type: TAG_TYPES.DROPDOWN_TAG_TYPE,
      label: 'select-creatives-label',
      value: formData.creatives,
      onChange: (value: string) => handlers.handleFieldChange(FORM_FIELDS.CREATIVES, value),
      placeholder: 'select-creatives',
      optional: true,
      options: getCreativesOptions(t),
      error: fieldErrors.creatives,
    },
  ];

  if (isEditMode)
    baseFields.push({
      id: 'gtm-status-field',
      type: TAG_TYPES.DROPDOWN_TAG_TYPE,
      label: 'gtm-status-label',
      value: formData.gtmStatus,
      onChange: (value: string) => handlers.handleFieldChange(FORM_FIELDS.GTM_STATUS, value),
      placeholder: 'gtm-status-placeholder',
      disabled: !(userRole === UserRoles.ADMIN || userRole === UserRoles.SUPER_ADMIN),
      options: getTranslatedGtmStatusOptions(t),
      error: fieldErrors.gtmStatus,
    });

  return baseFields;
};
